import { useCurrency } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import {
  AlertTriangle,
  HelpCircle,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export const AssetManagementSection: React.FC<{ data: any }> = ({ data }) => {
  const currency = useCurrency();
  const navigate = useNavigate();

  const getPriorityColor = (priority: "high" | "medium" | "low") => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTrendIcon = (trend: "increasing" | "decreasing" | "stable") => {
    if (trend === "increasing")
      return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (trend === "decreasing")
      return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <div className="w-4 h-4" />;
  };

  return (
    <>
      {/* High Maintenance Cost Assets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex item-center gap-2">
              <div>
                <CardTitle className="page-title">
                  High Maintenance Cost Assets
                </CardTitle>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      This asset is one of the top drivers of maintenance
                      spending. Click 'Review' to investigate the root cause of
                      its high cost.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.result?.highMaintenanceCostAssets &&
              data.result.highMaintenanceCostAssets.length > 0 ? (
                data.result.highMaintenanceCostAssets.map((asset, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium">{asset.assetName ?? "NA"}</p>
                        <p className="text-sm text-muted-foreground">
                          {currency} {asset.monthlyCost}
                          {asset.unit} monthly cost
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getTrendIcon(asset.trend)}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          asset.assetId
                            ? navigate(
                                `/ax/asset-registry/assets/view/${asset.assetId}`
                              )
                            : null
                        }
                      >
                        View
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted-foreground py-6">
                  No high maintenance cost assets available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Assets Approaching Replacement */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex item-center gap-2">
              <div>
                <CardTitle className="page-title">
                  Assets Approaching Replacement
                </CardTitle>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      This asset is nearing its planned end-of-life and will
                      require capital investment. Priority is based on risk and
                      urgency.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.result?.assetsApproachingReplacement &&
              data.result.assetsApproachingReplacement.length > 0 ? (
                data.result.assetsApproachingReplacement.map((asset, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {asset.priority === "high" && (
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium">{asset.assetName}</p>
                        <p className="text-sm text-muted-foreground">
                          {currency} {asset.replacementCost}K •{" "}
                          {asset.targetDate}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getPriorityColor(asset.priority)}>
                        {asset.priority}
                      </Badge>
                      <Button variant="ghost" size="sm">
                        Schedule
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted-foreground py-6">
                  No assets approaching replacement available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
